import * as React from "react"
import { cn } from "../../lib/utils"

/**
 * Native HTML Select components - immune to browser extension DOM manipulation errors.
 * API compatible with Radix UI Select for easy migration.
 */

const SelectContext = React.createContext({
  value: "",
  onValueChange: () => {},
  items: [],
  registerItem: () => {},
});

const Select = ({ children, value, onValueChange, defaultValue }) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");
  const [items, setItems] = React.useState([]);
  
  const actualValue = value !== undefined ? value : internalValue;
  
  const handleChange = (newValue) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onValueChange?.(newValue);
  };

  const registerItem = React.useCallback((itemValue, itemLabel) => {
    setItems(prev => {
      const exists = prev.some(item => item.value === itemValue);
      if (exists) return prev;
      return [...prev, { value: itemValue, label: itemLabel }];
    });
  }, []);

  return (
    <SelectContext.Provider value={{ value: actualValue, onValueChange: handleChange, items, registerItem }}>
      {children}
    </SelectContext.Provider>
  );
};

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  // SelectTrigger is not used in native select, but we keep it for API compatibility
  return null;
});
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = ({ placeholder }) => {
  // SelectValue is not used in native select, but we keep it for API compatibility
  return null;
};
SelectValue.displayName = "SelectValue";

const SelectContent = React.forwardRef(({ className, children, position = "popper", ...props }, ref) => {
  const { value, onValueChange } = React.useContext(SelectContext);
  const [localItems, setLocalItems] = React.useState([]);

  // Extract items from children
  React.useEffect(() => {
    const extractItems = (children) => {
      const items = [];
      React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;
        
        // Check if it's a SelectItem by displayName or by having a value prop
        const isSelectItem = child.type?.displayName === 'SelectItem' || 
                            (child.props?.value !== undefined && child.type !== 'option');
        
        if (isSelectItem) {
          const itemValue = child.props.value;
          let itemLabel = '';
          
          // Extract text content from children
          if (typeof child.props.children === 'string') {
            itemLabel = child.props.children;
          } else if (typeof child.props.children === 'number') {
            itemLabel = String(child.props.children);
          } else if (React.isValidElement(child.props.children)) {
            // Try to extract text from nested elements
            const extractText = (element) => {
              if (typeof element === 'string' || typeof element === 'number') {
                return String(element);
              }
              if (React.isValidElement(element) && element.props?.children) {
                return React.Children.toArray(element.props.children)
                  .map(extractText)
                  .join('');
              }
              if (Array.isArray(element)) {
                return element.map(extractText).join('');
              }
              return '';
            };
            itemLabel = extractText(child.props.children);
          } else if (Array.isArray(child.props.children)) {
            itemLabel = child.props.children
              .map(c => {
                if (typeof c === 'string' || typeof c === 'number') return String(c);
                if (React.isValidElement(c)) {
                  const extractText = (el) => {
                    if (typeof el === 'string' || typeof el === 'number') return String(el);
                    if (el?.props?.children) {
                      return React.Children.toArray(el.props.children).map(extractText).join('');
                    }
                    return '';
                  };
                  return extractText(c);
                }
                return '';
              })
              .join('');
          }
          
          if (itemValue !== undefined) {
            items.push({ value: itemValue, label: itemLabel || itemValue });
          }
        } else if (child.props?.children) {
          // Recursively check children (for SelectGroup)
          items.push(...extractItems(child.props.children));
        }
      });
      return items;
    };

    const extracted = extractItems(children);
    setLocalItems(extracted);
  }, [children]);

  return (
    <select
      ref={ref}
      value={value || ""}
      onChange={(e) => onValueChange(e.target.value)}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "cursor-pointer",
        className
      )}
      {...props}
    >
      <option value="">Seleccionar...</option>
      {localItems.map((item, index) => (
        <option key={item.value || index} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
});
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(({ className, children, value, ...props }, ref) => {
  // This component is only used to pass data to SelectContent
  // Return null but keep the component for API compatibility
  return null;
});
SelectItem.displayName = "SelectItem";

const SelectGroup = ({ children }) => <>{children}</>;
SelectGroup.displayName = "SelectGroup";

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => null);
SelectLabel.displayName = "SelectLabel";

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => null);
SelectSeparator.displayName = "SelectSeparator";

const SelectScrollUpButton = () => null;
const SelectScrollDownButton = () => null;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
